import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TuiDialogService} from '@taiga-ui/core';
import {
  EffectiveFeePlanResponse,
  FeePlan,
  FeeRule,
  FeeTier,
  FlowDirection,
} from '@gatewee/types/fee';
import {PaymentMethod} from '@gatewee/types/payment';
import {Subscription} from 'rxjs';

@Component({
  selector: 'gatewee-fees-settings-v2',
  templateUrl: './gatewee-fees-settings-v2.component.html',
  styleUrls: ['./gatewee-fees-settings-v2.component.css'],
  standalone: false,
})
export class GateweeFeesSettingsV2Component implements OnChanges {
  @Input() recipientId = 0;
  @Input() effectivePlan: EffectiveFeePlanResponse | null = null;
  @Input() availablePlans: FeePlan[] = [];
  @Input() loading = false;

  @ViewChild('changePlanDialog') changePlanDialog!: TemplateRef<never>;

  @Output() readonly assignPlan = new EventEmitter<number>();
  @Output() readonly removeAssignment = new EventEmitter<void>();

  planSelectItems: {label: string; value: number}[] = [];
  private changePlanDialogSub?: Subscription;

  readonly assignForm = new FormGroup({
    planId: new FormControl<number | null>(null),
  });

  constructor(
    private readonly translate: TranslateService,
    private readonly dialogService: TuiDialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availablePlans']) {
      this.rebuildPlanItems();
    }
  }

  get currentPlan(): FeePlan | null {
    return this.effectivePlan?.plan ?? null;
  }

  get source(): 'assignment' | 'default' | 'none' | null {
    return this.effectivePlan?.source ?? null;
  }

  get currentPlanRulesCount(): number {
    return this.currentPlan?.rules?.length ?? 0;
  }

  get currentPlanTiersCount(): number {
    return this.countTiers(this.currentPlan);
  }

  openChangePanel(): void {
    const id = this.currentPlan?.id ?? null;
    this.assignForm.patchValue({planId: id});
    this.changePlanDialogSub?.unsubscribe();
    this.changePlanDialogSub = this.dialogService
      .open(this.changePlanDialog, {
        size: 'm',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.changePlanDialogSub = undefined;
          this.assignForm.reset({planId: null});
        },
      });
  }

  cancelChange(close: unknown): void {
    this.closeChangePlanDialog(close);
  }

  closeChangePlanDialog(close: unknown): void {
    if (typeof close === 'function') {
      close();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
    this.changePlanDialogSub?.unsubscribe();
    this.changePlanDialogSub = undefined;
    this.assignForm.reset({planId: null});
  }

  confirmAssign(close: unknown): void {
    const raw = this.assignForm.get('planId')?.value;
    const id = this.extractPlanId(raw);
    if (id === null || !this.hasChangesToAssign) return;
    this.assignPlan.emit(id);
    this.closeChangePlanDialog(close);
  }

  requestRemoveAssignment(): void {
    this.removeAssignment.emit();
  }

  getTierPreview(tier: FeeTier, amount = 500): string {
    const fixed = this.toNumber(tier.fixedAmount) ?? 0;
    const rate = this.toNumber(tier.rateValue) ?? 0;
    const total = fixed + (amount * rate) / 100;
    return this.formatCurrency(total);
  }

  formatCurrencyOrDash(value: unknown): string {
    const numberValue = this.toNumber(value);
    if (numberValue === null) return '-';
    return this.formatCurrency(numberValue);
  }

  formatRateOrDash(value: unknown): string {
    const numberValue = this.toNumber(value);
    if (numberValue === null) return '-';
    return `${numberValue.toFixed(2)}%`;
  }

  trackByTier(index: number, tier: FeeTier): string {
    return [
      index,
      tier.minAmount,
      tier.maxAmount,
      tier.fixedAmount,
      tier.rateValue,
    ].join('|');
  }

  ruleLabel(rule: FeeRule): string {
    const opLabel =
      rule.operation === FlowDirection.INCOME
        ? this.translate.instant('features.feePlans.form.operationIncome')
        : this.translate.instant('features.feePlans.form.operationCashOut');
    const parts: string[] = [opLabel];
    if (rule.paymentMethod !== null && rule.paymentMethod !== undefined) {
      parts.push(
        this.translate.instant(`enums.paymentMethod.${rule.paymentMethod}`),
      );
    } else {
      parts.push(
        this.translate.instant('features.feePlans.form.paymentMethodAny'),
      );
    }
    if (rule.paymentMethod === PaymentMethod.CREDIT_CARD) {
      if (rule.installments !== null && rule.installments !== undefined) {
        parts.push(`${rule.installments}x`);
      } else {
        parts.push(
          this.translate.instant('features.feePlans.form.installmentsAny'),
        );
      }
    }
    if (rule.brand) {
      parts.push(rule.brand);
    }
    return parts.join(' · ');
  }

  sourceLabelKey(): string {
    const s = this.source;
    if (!s) return 'features.feePlans.assignment.source.none';
    return `features.feePlans.assignment.source.${s}`;
  }

  get selectedPlanSummary(): string {
    const plan = this.selectedPlan;
    if (!plan) return '';
    const methods = this.collectMethods(plan);
    const operations = this.collectOperations(plan);
    const methodsSummary = methods.length > 0 ? methods.join(', ') : 'qualquer';
    const operationsSummary =
      operations.length > 0 ? operations.join(', ') : 'qualquer';
    return `${plan.rules?.length ?? 0} regras, ${this.countTiers(
      plan,
    )} faixas, metodos: ${methodsSummary}, operacoes: ${operationsSummary}`;
  }

  get hasChangesToAssign(): boolean {
    const selectedId = this.extractPlanId(this.assignForm.get('planId')?.value);
    const currentId = this.currentPlan?.id ?? null;
    return selectedId !== null && selectedId !== currentId;
  }

  get selectedPlanStatusLabel(): string {
    const status = this.selectedPlan?.status;
    return status ?? '-';
  }

  get selectedPlanRulesCount(): number {
    return this.selectedPlan?.rules?.length ?? 0;
  }

  get selectedPlanTiersCount(): number {
    return this.countTiers(this.selectedPlan);
  }

  get selectedPlanMethodsSummary(): string {
    const methods = this.collectMethods(this.selectedPlan);
    return methods.length > 0 ? methods.join(', ') : 'qualquer';
  }

  get selectedPlanOperationsSummary(): string {
    const operations = this.collectOperations(this.selectedPlan);
    return operations.length > 0 ? operations.join(', ') : 'qualquer';
  }

  private rebuildPlanItems(): void {
    this.planSelectItems = (this.availablePlans ?? []).map(p => ({
      label: p.name,
      value: p.id,
    }));
  }

  private extractPlanId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (
      value &&
      typeof value === 'object' &&
      'value' in (value as Record<string, unknown>)
    ) {
      return this.extractPlanId((value as {value: unknown}).value);
    }
    return null;
  }

  private get selectedPlan(): FeePlan | null {
    const selectedId = this.extractPlanId(this.assignForm.get('planId')?.value);
    if (selectedId === null) return null;
    return this.availablePlans.find(plan => plan.id === selectedId) ?? null;
  }

  private countTiers(plan: FeePlan | null): number {
    if (!plan?.rules?.length) return 0;
    return plan.rules.reduce(
      (total, rule) => total + (rule.tiers?.length ?? 0),
      0,
    );
  }

  private collectMethods(plan: FeePlan | null): string[] {
    if (!plan?.rules?.length) return [];
    const methods = [
      ...new Set(
        plan.rules
          .map(rule => rule.paymentMethod)
          .filter(
            (method): method is PaymentMethod =>
              method !== null && method !== undefined,
          ),
      ),
    ];
    return methods.map(method =>
      this.translate.instant(`enums.paymentMethod.${method}`),
    );
  }

  private collectOperations(plan: FeePlan | null): string[] {
    if (!plan?.rules?.length) return [];
    const operations = [...new Set(plan.rules.map(rule => rule.operation))];
    return operations.map(operation =>
      operation === FlowDirection.INCOME
        ? this.translate.instant('features.feePlans.form.operationIncome')
        : this.translate.instant('features.feePlans.form.operationCashOut'),
    );
  }

  private formatCurrency(value: number): string {
    return `R$ ${value.toFixed(2)}`;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
}
